import { Modal, Form, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { createApiService } from "./shared/apiConfig";
import {
  MonitorStripeApiApi,
  ServerTransferConnectedAccountDropdown,
} from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { Select } from "./Controls/Select";
import { useNavigate } from "react-router-dom";
import { Copy } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { toast } from "react-toastify";

interface PayoutAccountModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onSave: (data: PayoutAccountForm) => void;
  selectedPayoutAccount: ServerTransferConnectedAccountDropdown | undefined;
  facilityId: number;
  facilityName: string;
  isNewFacility?: boolean;
}

export interface PayoutAccountForm {
  payoutAccountId: string;
  payoutAccountName: string;
}

function ClipboardIcon({ payload, tooltipText, icon }: any) {
  const handleCopy = () => {
    navigator.clipboard.writeText(payload);
  };

  return (
    <OverlayTrigger
      trigger="click"
      placement="top"
      overlay={<Tooltip id="clipboard-tooltip">{tooltipText}</Tooltip>}
      rootClose
    >
      <span role="button" onClick={handleCopy} style={{ cursor: "pointer" }}>
        {icon}
      </span>
    </OverlayTrigger>
  );
}

export function PayoutAccountModal({
  show,
  setShow,
  onSave,
  selectedPayoutAccount,
  facilityId,
  isNewFacility,
  facilityName,
}: PayoutAccountModalProps) {
  const { register, watch, handleSubmit, setValue, reset } =
    useForm<PayoutAccountForm>({
      defaultValues: {
        payoutAccountId: selectedPayoutAccount?.id ?? "",
        payoutAccountName: selectedPayoutAccount?.label ?? "",
      },
    });

  const [connectedAccounts, setConnectedAccounts] = useState<
    ServerTransferConnectedAccountDropdown[]
  >([]);
  const [onboardingLink, setOnboardingLink] = useState<string>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [changed, setChanged] = useState(false);
  const nav = useNavigate();

  const stripeApi = createApiService(MonitorStripeApiApi);
  const payoutAccount = watch("payoutAccountId");

  useEffect(() => {
    if (show) {
      stripeApi.monitorStripeAccounts().then((res) => {
        setConnectedAccounts(res.data);
      });

      if (!isNewFacility) {
        refreshAccount();
      }
    }
  }, [show, facilityId]);

  async function refreshAccount() {
    const accountsRes = await stripeApi.monitorStripeAccounts();
    setConnectedAccounts(accountsRes.data);

    const facilityRes = await stripeApi.monitorStripeFacilityIdLinkGet({
      facilityId,
    });
   
    if (!facilityRes.data.isOnboarded) {
      setOnboardingLink(facilityRes.data.connectLink!);
    } else {
      setOnboardingLink(undefined);
    }
  }

  async function submit(data: PayoutAccountForm) {
    // If selecting New, generate onboarding link and save
    if (!selectedAccount){
        const res = await stripeApi.monitorStripeCreateNewAccount({
          facilityName,
          facilityId
        });
        const newAccount = {
          id: res.data.id!,
          label: res.data.label,
        };
        setConnectedAccounts((prev) => [...prev, newAccount]);
        setValue("payoutAccountId", newAccount.id, {
          shouldValidate: true,
        });
        setValue("payoutAccountName", newAccount.label!, {
          shouldValidate: true,
        });
        setOnboardingLink(res.data.linkUrl!);
        
        // Update data with the new account details
        data.payoutAccountId = newAccount.id;
        data.payoutAccountName = newAccount.label!;
        onSave(data);
        return;
    }

    onSave(data);
    setShow(false);
    if (isNewFacility) nav("/operator/facilities");
    setChanged(false);
  }

  function onClose() {
    reset();
    setShow(false);
    setChanged(false);
    if (isNewFacility) nav("/operator/facilities");
  }

  const selectedAccount = connectedAccounts.find(
    (acc) => acc.id === payoutAccount
  );

  return (
    <Modal show={show} centered onHide={() => onClose()}>
      <Form
        onSubmit={handleSubmit((data) => {
          setPendingData(data);
          if (!selectedAccount) {
            submit(data);
            return;
          }
          setShowConfirm(true);
        })}
      >
        <Modal.Header>Edit Payout Account</Modal.Header>
        <Modal.Body>
          <Select
            label="Payout Account"
            value={payoutAccount}
            {...register("payoutAccountId", {
              onChange: (e) => {
                const accountId = e.target.value;
                const account = connectedAccounts.find(
                  (x) => x.id === accountId
                );
                setValue("payoutAccountId", accountId, {
                  shouldValidate: false,
                });
                if (account) {
                  setValue("payoutAccountName", account.label!, {
                    shouldValidate: false,
                  });
                }
                setOnboardingLink(undefined);
                setChanged(true);
              },
            })}
          >
            {connectedAccounts.map((acc) => (
              <option key={acc.id} value={acc.id!}>
                {acc.label}
              </option>
            ))}
            <option value="">➕ Create New</option>
          </Select>

         
          {onboardingLink && (
            <Alert variant="info" className="mt-3">
              <div className="mb-2">
                <a
                  href={onboardingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="fw-semibold text-decoration-none text-primary"
                >
                  Complete Stripe Onboarding
                </a>
              </div>

              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={onboardingLink}
                  readOnly
                  onFocus={(e) => e.target.select()}
                />
                <span className="input-group-text">
                  <ClipboardIcon
                    payload={onboardingLink}
                    tooltipText="Link copied!"
                    icon={<Copy className="small" />}
                  />
                </span>
              </div>
              <div className="mt-3 text-muted">
                Please note this is a one-time link and will expire in 5 minutes.  A new link will be generated automatically if you don't follow the onboarding link within 5 minutes.
              </div>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button onClick={() => onClose()}>Close</Button>
            <Button
              type="submit"
              className="btn-main ms-2"
              disabled={!changed && !!selectedAccount}
            >
              Save
            </Button>
          </div>
          {selectedAccount && !selectedAccount.isAccountOnboarded && (
            <Button
              variant="outline-primary"
              onClick={async () => {
                await refreshAccount();
                toast.success("Status refreshed!");
              }}
            >
              {onboardingLink ? "Refresh" : "Generate Onboarding Link"}
            </Button>
          )}
        </Modal.Footer>
      </Form>

      <ConfirmModal
        title="Change Payout Account"
        message="Are you sure you want to change the account? This will affect all future payments."
        show={showConfirm}
        setShow={setShowConfirm}
        onConfirm={() => {
          if (pendingData) {
            submit(pendingData);
            setPendingData(null);
            setShow(false);
          }
        }}
      />
    </Modal>
  );
}
